import React, { useCallback, useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { ArrowsPointingOutIcon } from '@heroicons/react/24/outline'
import { GeoPoint } from '../lib/geocode'

// Leaflet map for the delivery board. Plain Leaflet driven from effects rather than
// react-leaflet: ~100 lines of glue, no second dependency, and no coupling between the
// React major and a wrapper's supported range.
//
// Tiles come from OpenStreetMap's public servers — free, no key. Fine for a handful of
// terminals viewing one city; attribution is required and Leaflet renders it.

export interface DeliveryPin {
  id: number
  position: GeoPoint
  // false = PC6 centroid rather than the door; drawn with a dashed border so the operator
  // knows the pin is "this street or the next", not the house.
  precise: boolean
  // Tailwind background literals (JIT-safe: every value used exists as a literal in the
  // POS source). laneBg = the order's status; groupBg = its PC4 bundle group, if any.
  laneBg: string
  groupBg: string | null
  // ASAP draws a round pin, a pre-order a wider pill, a stop in a planned or active
  // delivery route a SQUARE — the shape is the type, readable across the map before any
  // text is, and a square can never be mistaken for a suggestion.
  kind: 'asap' | 'preorder' | 'stop'
  // What the pin carries: an ASAP order's suggested rank ("3"), a pre-order's arrival
  // time ("18:45"), or a route stop's number ("2"). Plain text — escaped before it
  // reaches the icon's HTML.
  badge: string
  // Hover tooltip, plain text — escaped here before it reaches Leaflet's HTML tooltip.
  label: string
}

interface DeliveryMapProps {
  pins: DeliveryPin[]
  home: GeoPoint | null
  selectedId: number | null
  onSelect: (id: number) => void
  className?: string
}

// Groningen centre — only until the home pin or the first order pin fits the view.
const FALLBACK_CENTER: L.LatLngTuple = [53.219, 6.568]
const FALLBACK_ZOOM = 13
const TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'
const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors'
const FIT_OPTIONS: L.FitBoundsOptions = { padding: [48, 48], maxZoom: 16 }

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string)

// Full class literals per variant — Tailwind's scanner cannot see a computed size.
const PIN_SIZES = {
  asap: { outer: 'w-10 h-10 rounded-full', inner: 'w-7 h-7 rounded-full', icon: [40, 40] as L.PointTuple, anchor: [20, 20] as L.PointTuple },
  preorder: { outer: 'w-16 h-10 rounded-full', inner: 'h-7 px-1.5 rounded-full', icon: [64, 40] as L.PointTuple, anchor: [32, 20] as L.PointTuple },
  stop: { outer: 'w-10 h-10 rounded-lg', inner: 'w-7 h-7 rounded-md', icon: [40, 40] as L.PointTuple, anchor: [20, 20] as L.PointTuple },
}

const pinIcon = (pin: DeliveryPin, selected: boolean): L.DivIcon => {
  const size = PIN_SIZES[pin.kind]
  return L.divIcon({
    className: '',
    // Ring (group colour) → white gap (inner border) → body (lane colour) → the badge
    // text. The gap is what keeps a ring readable when an area happens to hash to the
    // same hue as the lane; every lane colour is a 500/600 shade, so white text carries.
    html: `<div class="${size.outer} flex items-center justify-center transition-transform ${
      pin.groupBg ?? ''
    } ${selected ? 'scale-125' : ''}"><div class="${
      size.inner
    } border-2 border-white shadow-md flex items-center justify-center ${pin.laneBg} ${
      pin.precise ? '' : 'border-dashed'
    } ${
      selected ? 'ring-2 ring-gray-900' : ''
    }"><span class="text-[11px] font-black text-white leading-none tracking-tight">${escapeHtml(
      pin.badge
    )}</span></div></div>`,
    iconSize: size.icon,
    iconAnchor: size.anchor,
    tooltipAnchor: [0, -18],
  })
}

const HOME_ICON = L.divIcon({
  className: '',
  html: `<div class="w-8 h-8 rounded-lg bg-gray-900 text-white flex items-center justify-center shadow-md border-2 border-white"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  tooltipAnchor: [0, -14],
})

const DeliveryMap: React.FC<DeliveryMapProps> = ({ pins, home, selectedId, onSelect, className }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layerRef = useRef<L.LayerGroup | null>(null)
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  // Latest pins/home for the resize handler and the button, without re-registering either.
  const pinsRef = useRef(pins)
  pinsRef.current = pins
  const homeRef = useRef(home)
  homeRef.current = home
  // Pin ids the view has already been fitted to — a NEW pin always earns a refit, so an
  // order that arrives while the operator is zoomed in cannot be missed.
  const fittedIdsRef = useRef(new Set<number>())
  // Whether the operator has panned/zoomed since the last automatic fit. While they have
  // not, the map keeps every pin in view on its own (orders leaving, container resizing);
  // once they have, their framing is respected until a new pin appears or they tap
  // "Show all". Programmatic moves must not count as theirs — see `fittingRef`.
  const userMovedRef = useRef(false)
  const fittingRef = useRef(false)

  // Fit every pin (and home) into view. The default policy of the map: nothing off-screen.
  const fitAll = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    const points: L.LatLngTuple[] = pinsRef.current.map(p => [p.position.lat, p.position.lng])
    const home = homeRef.current
    if (home) points.push([home.lat, home.lng])
    fittingRef.current = true
    if (points.length === 0) map.setView(FALLBACK_CENTER, FALLBACK_ZOOM)
    else if (points.length === 1 && !pinsRef.current.length) map.setView(points[0], FALLBACK_ZOOM)
    else map.fitBounds(L.latLngBounds(points), FIT_OPTIONS)
    fittingRef.current = false
    userMovedRef.current = false
    for (const p of pinsRef.current) fittedIdsRef.current.add(p.id)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const map = L.map(container, { center: FALLBACK_CENTER, zoom: FALLBACK_ZOOM, zoomControl: true })
    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 19 }).addTo(map)
    const layer = L.layerGroup().addTo(map)
    mapRef.current = map
    layerRef.current = layer

    // Leaflet fires movestart for its own fitBounds/setView too; only a move that is not
    // ours means the operator took the wheel.
    map.on('movestart', () => {
      if (!fittingRef.current) userMovedRef.current = true
    })

    // The board's map pane is sized by flex; Leaflet only measures on load, so a lane/map
    // switch or a window resize would otherwise leave grey tiles at the edges — and a fit
    // computed against a not-yet-laid-out (zero-height) container lands on the wrong zoom.
    // After every resize the view is re-fitted, unless the operator has taken over.
    const observer = new ResizeObserver(() => {
      map.invalidateSize()
      if (!userMovedRef.current) fitAll()
    })
    observer.observe(container)

    return () => {
      observer.disconnect()
      map.remove()
      mapRef.current = null
      layerRef.current = null
      fittedIdsRef.current = new Set()
      userMovedRef.current = false
    }
  }, [fitAll])

  useEffect(() => {
    const map = mapRef.current
    const layer = layerRef.current
    if (!map || !layer) return
    layer.clearLayers()

    if (home) {
      // Interactive (the default) so the tooltip works; it has no click handler.
      L.marker([home.lat, home.lng], { icon: HOME_ICON, zIndexOffset: -100 })
        .bindTooltip('Curry Me', { direction: 'top' })
        .addTo(layer)
    }
    for (const pin of pins) {
      const selected = pin.id === selectedId
      L.marker([pin.position.lat, pin.position.lng], {
        icon: pinIcon(pin, selected),
        zIndexOffset: selected ? 1000 : 0,
      })
        .bindTooltip(escapeHtml(pin.label), { direction: 'top' })
        .on('click', () => onSelectRef.current(pin.id))
        .addTo(layer)
    }

    const fitted = fittedIdsRef.current
    const hasNewPin = pins.some(p => !fitted.has(p.id))
    const setChanged = hasNewPin || fitted.size !== pins.length
    // A new pin always refits (must not be missed). Anything else — a pin leaving, home
    // resolving — refits only while the operator has not framed the map themselves. That
    // guard covers the home pin too: on a map with no order pins nothing is ever "fitted",
    // so without it every poll snapped an operator's pan or zoom back to the restaurant.
    if (hasNewPin || ((setChanged || (home && fitted.size === 0)) && !userMovedRef.current)) {
      fitAll()
    }
    if (!hasNewPin && !setChanged) return
    fittedIdsRef.current = new Set(pins.map(p => p.id))
  }, [pins, home, selectedId, fitAll])

  // The map div carries `isolate` so Leaflet's internal z-indexes (400+) cannot climb over
  // the POS header; the button sits beside it in the wrapper, above the map.
  return (
    <div className={`relative ${className ?? ''}`}>
      <div ref={containerRef} className="absolute inset-0 isolate z-0" />
      <button
        type="button"
        onClick={fitAll}
        className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/95 shadow-md border border-gray-200 text-xs font-black uppercase tracking-wide text-gray-800 hover:bg-white"
        title="Zoom out so every order is on screen"
      >
        <ArrowsPointingOutIcon className="w-4 h-4" />
        Show all{pins.length > 0 ? ` (${pins.length})` : ''}
      </button>
    </div>
  )
}

export default DeliveryMap
